import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useEffect, useState } from 'react';
import { ViewName } from '../../singleView/components/ViewName';
import { ViewTab } from '../../tabs/components/ViewTab';
import { ViewTabsContainer } from '../../tabs/components/ViewTabsContainer';
import { usePinnedViewLimit } from '../../viewLimits/hooks/usePinnedViewLimit';
import { useAutoPinCurrentView } from '../hooks/useAutoPinCurrentView';
import { useFetchPinnedViews } from '../hooks/useFetchPinnedViews';
import { PinnedViewsLoadingSkeleton } from './PinnedViewsLoadingSkeleton';
import { useSetPinnedViews } from '../hooks/useSetPinnedViews';
import { AbovePinnedViewLimitAlert } from './AbovePinnedViewLimitAlert';
import { PinnedViewsErrorSkeleton } from './PinnedViewsErrorSkeleton';
import { useRemovePinnedView } from '../hooks/useRemovePinnedView';
import Raven from 'raven-js';
import emptyFunction from 'react-utils/emptyFunction';

/**
 * This type represents "what is the currently active view tab within the pinned views bar?"
 * This is intentionally nullable rather than optional.
 * We require consumers pass currentViewId at all times, if they do not, it is a bug
 * However, consumers can explicitly set currentViewId=null, meaning "There is no currently active tab at all"
 * An example of this is within FLL's "Folders" tab
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * This is passed down into react-beautiful-dnd logic
 */
const dndReorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

/**
 * this function is meant to take into account all loading/error states for getting the backend view ids
 * as well as all business logic around what consumers pass to us as currentViewId, and then return an array of strings that represent view ids that is safe to map over for rendering view tabs
 */
export const transformBackendViewIdsAndCurrentViewIdToPinnedViewIdsArray = ({
  idsFromApi,
  currentViewId
}) => {
  if (!idsFromApi) {
    return [];
  }
  if (currentViewId === null) {
    return idsFromApi;
  }
  const idsAsSet = new Set(idsFromApi);
  idsAsSet.add(currentViewId);
  return Array.from(idsAsSet);
};

/**
 * <PinnedViews
 *  objectTypeId={CONTACT_TYPE_ID} //ex - Im showing users views for the contacts object type
 *  namespace={VIEWS_NAMESPACE_FORMS} //ex - Im showing users views for the forms application
 *  currentViewId="viewIdFromYourAppsUrl" //ex - A user navigated to /myapp/views/1234 - tell this component the user is looking at view 1234
 *  onCurrentViewIdChange={functionToUpdateYourViewIdInUrl} //ex - the user clicked on a different view tab, update the apps url (and this will flow back down to currentViewId
 *  onPrefetchViews={(viewIds) => viewIds.forEach(viewId => viewsApi.get(viewId).then(view => storeInMyAppsCache))} //ex - This component has shown views A, B, and C to the user. Your app might want to fetch those views and cache them to provide a snappier UX and less loading times when they click on the view tabs
 * />
 *
 **/

export const PinnedViews = ({
  objectTypeId,
  namespace,
  currentViewId,
  onCloseViewTab = emptyFunction,
  onCurrentViewIdChange,
  onPrefetchViews,
  onReorderViewTabs = emptyFunction,
  ViewNameImplementation = ViewName,
  tabStyle = 'legacy'
}) => {
  const [viewsScrollContainer, setViewsScrollContainer] = useState(null);
  const {
    data: pinnedViewsLimit,
    loading: pinnedViewsLimitLoading,
    error: pinnedViewsLimitError
  } = usePinnedViewLimit();
  const {
    data,
    loading: pinnedViewsLoading,
    error: pinnedViewsError
  } = useFetchPinnedViews({
    objectTypeId,
    namespace
  });
  const removeView = useRemovePinnedView({
    objectTypeId,
    namespace,
    currentViewId,
    onCurrentViewIdChange
  });
  const handleCloseView = useCallback((...args) => {
    onCloseViewTab(...args);
    removeView(...args);
  }, [onCloseViewTab, removeView]);
  const [setPinnedViews] = useSetPinnedViews({
    objectTypeId,
    namespace
  });

  //this will remove a pinned view
  //it take into account if the user is removing the view they are looking at, and what one they should be moved to

  const onReorderInternal = useCallback(result => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    onReorderViewTabs();
    const currentValue = data && data.pinnedViews || [];
    const newValue = dndReorder(currentValue, result.source.index, result.destination.index);
    setPinnedViews(newValue).catch(e => {
      Raven.captureException(e);
    });
  }, [data, onReorderViewTabs, setPinnedViews]);

  //this will automatically scroll the active view id into screen when the active view changes
  //this solves for when the user has a lot of pinned views, and they first open the app
  //and the "active" view is all the way to the right
  //This will automatically scroll the active view into a viewable section of the pinned views bar
  const activeTabScrollToRefCallback = useCallback(currentTabEl => {
    if (currentTabEl && viewsScrollContainer) {
      // Get the container's viewport boundaries
      const containerRect = viewsScrollContainer.getBoundingClientRect();
      const tabRect = currentTabEl.getBoundingClientRect();

      // Check if the tab is not fully visible
      const isNotFullyVisible = tabRect.left < containerRect.left || tabRect.right > containerRect.right;
      if (isNotFullyVisible) {
        // currentTabEl has a non-offsetParent parent with a border
        const leftBorderWidth = 1;
        viewsScrollContainer.scrollTo({
          left: currentTabEl.offsetLeft - leftBorderWidth
        });
      }
    }
  }, [viewsScrollContainer]);
  useAutoPinCurrentView({
    currentViewId,
    namespace,
    objectTypeId
  });
  const pinnedViewIds = transformBackendViewIdsAndCurrentViewIdToPinnedViewIdsArray({
    idsFromApi: data ? data.pinnedViews : [],
    currentViewId
  });

  // this useEffect will inform consumers
  // - Hey, Im showing the user these pinned views,
  //   good chance they'll click on them.
  //   Im letting you know the ids of the views
  //   Im showing to them so you can prefetch them
  useEffect(() => {
    if (onPrefetchViews) {
      onPrefetchViews({
        viewIds: pinnedViewIds
      });
    }
  }, [namespace, objectTypeId, pinnedViewIds, onPrefetchViews]);
  if (!data || pinnedViewsLoading || pinnedViewsLimitLoading) {
    return /*#__PURE__*/_jsx(PinnedViewsLoadingSkeleton, {
      tabStyle: tabStyle
    });
  }
  if (pinnedViewsError || pinnedViewsLimitError) {
    return /*#__PURE__*/_jsx(PinnedViewsErrorSkeleton, {});
  }
  const isAbovePinnedViewLimit = pinnedViewsLimit !== undefined && pinnedViewIds.length > pinnedViewsLimit;
  return /*#__PURE__*/_jsx(ViewTabsContainer, {
    onReorder: onReorderInternal,
    containerRef: setViewsScrollContainer,
    tabStyle: tabStyle,
    children: pinnedViewIds.map((viewId, index) => {
      const isActive = currentViewId !== null && currentViewId.toString() === viewId;
      const key = `view-tab-${viewId}`;
      const closeDisabled = data.pinnedViews.length < 2;
      const closeTooltip = closeDisabled ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "viewsManagement.portalViews.viewLimits.oneViewRequired"
      }) : undefined;
      return /*#__PURE__*/_jsxs(ViewTab, {
        isDragDisabled: isAbovePinnedViewLimit,
        tabId: viewId,
        index: index,
        active: isActive,
        onCloseClick: handleCloseView,
        onTabClick: onCurrentViewIdChange,
        closeDisabled: closeDisabled,
        closeTooltip: closeTooltip,
        scrollToRef: isActive ? activeTabScrollToRefCallback : undefined,
        tabStyle: tabStyle,
        children: [isActive && isAbovePinnedViewLimit && /*#__PURE__*/_jsx(AbovePinnedViewLimitAlert, {}), /*#__PURE__*/_jsx(ViewNameImplementation, {
          objectTypeId: objectTypeId,
          viewId: viewId,
          namespaceName: namespace
        })]
      }, key);
    })
  });
};